// Copyright ©️ 2026 eVolve MEP, LLC

import { useEffect, useMemo, useState } from 'react';

import {
  type BendData,
  type BendTypeName,
  extractErrorMessage,
  isCanceledApiRequest,
  type MachineElement,
  useEvolveGet,
} from '@evolvemep/api-ts';
import { Flex, Group, LoadingOverlay, Stack, Text } from '@mantine/core';

import { parseMeasurement } from 'components/Mantine/InchesInput';
import { isNil, isNotNil } from 'helpers/isNotNil';
import { BendPreview } from 'modules/Shop/EvolveFabrication/BendCreator/BendPreview/BendPreview';
import { type BendResultState, BendResults } from 'modules/Shop/EvolveFabrication/BendCreator/BendPreview/BendResults';
import { getBasicBendSpecifications } from 'modules/Shop/EvolveFabrication/BendCreator/BendPreview/helpers';
import { BendPreviewContextProvider } from 'modules/Shop/EvolveFabrication/BendCreator/BendPreview/useBendPreviewContext';

import { BendCustomProperties } from './BendCustomProperties';
import { BendDataCard } from './BendDataCard';
import { BendDataOutput } from './BendDataOutput';

/**
 * Transforms a bend data response to bend preview dimensions for 3D previews
 */
export const transformBendData = (res: BendData, element: MachineElement) => {
  const { bendRadius } = res;
  const conduitSizeInches = parseMeasurement(element.materialSize, false, 'fraction');
  if (isNil(conduitSizeInches)) return null;
  const baseBend = getBasicBendSpecifications({ bendRadius, conduitSize: conduitSizeInches / 12 });
  res.finalDimensions.forEach(({ keyName, value }) => {
    baseBend[keyName] = value;
  });
  baseBend.layoutDimensions = {
    overallLength: element.adjustedLength ?? element.length,
    marks: res.layoutDimensions,
  };
  if (isNotNil(res.ybcData)) {
    baseBend.ybcString = res.ybcData;
  }
  return baseBend;
};

const bendTypesWhere3dIsPreferred: BendTypeName[] = [
  'Custom',
  'Kick 90 Left',
  'Kick 90 Right',
  'Kick 90 Back to Back Left',
  'Kick 90 Back to Back Right',
];

type Props = {
  element: MachineElement;
  onBendUpdated: (updatedBend: MachineElement) => void;
};

export const BendQAModalContents = ({ element, onBendUpdated }: Props) => {
  const { apiCall: getBendData, loading: loadingBendData } = useEvolveGet(
    `shop/fabrication/element/${element.elementId}/bendData`,
  );
  const [bendResult, setBendResult] = useState<BendResultState | null>(null);

  useEffect(() => {
    setBendResult(null);
    void getBendData({
      url: `shop/fabrication/element/${element.elementId}/bendData`,
    })
      .then((data) => {
        setBendResult({ isSuccess: true, data });
      })
      .catch((e) => {
        if (isCanceledApiRequest(e)) return;
        setBendResult({ isSuccess: false, message: extractErrorMessage(e) });
      });
  }, [element.elementId, getBendData]);

  const bendPreviewData = useMemo(() => {
    if (isNil(bendResult) || !bendResult.isSuccess) return null;
    return transformBendData(bendResult.data, element);
  }, [bendResult, element]);

  const isCrippa = element.benderProfile?.profileType === 'crippa150le';

  return (
    <BendPreviewContextProvider>
      <Group h="100%" wrap="nowrap" align="stretch" gap={0} style={{ overflow: 'hidden' }}>
        <Stack p="md" w={350} maw={350} bg="gray.1" style={{ overflowY: 'auto' }} flex="0 0 auto" h="100%">
          <BendDataCard title="Bend Details">
            <BendDataOutput
              label="Machine profile"
              value={element.benderProfile?.name ?? 'No machine profile selected for this element.'}
              textProps={isNil(element.benderProfile) ? { c: 'red' } : undefined}
            />
            <BendDataOutput
              label="Machine"
              value={element.benderProfile?.machineName ?? 'No machine profile selected for this element.'}
              textProps={isNil(element.benderProfile) ? { c: 'red' } : undefined}
            />
            <BendDataOutput label="Bend Type" value={element.bendType} />
            <BendDataOutput label="Conduit Type" value={element.materialType} />
            <BendDataOutput label="Conduit Size" value={element.materialSize} />
          </BendDataCard>
          <Stack style={{ position: 'relative' }}>
            <LoadingOverlay visible={loadingBendData} />
            {isNotNil(bendResult) && <BendResults element={element} bendResult={bendResult} showAllMarks={!isCrippa} />}
          </Stack>
          <BendCustomProperties element={element} onPropertiesUpdated={onBendUpdated} />
        </Stack>

        <Flex p="md" flex={1} justify="center" align="center" h="100%" style={{ overflow: 'hidden' }}>
          {isNotNil(bendResult) && bendResult.isSuccess ? (
            <BendPreview
              element={element}
              bendType={bendResult.data.bendType}
              bendPreviewData={bendPreviewData}
              initialPreviewMode={bendTypesWhere3dIsPreferred.includes(bendResult.data.bendType) ? '3d' : '2dFront'}
              hideLayoutButton={isCrippa}
            />
          ) : (
            <Stack justify="center">
              {bendResult?.isSuccess === false ? <Text c="red">{bendResult.message}</Text> : <LoadingOverlay visible />}
            </Stack>
          )}
        </Flex>
      </Group>
    </BendPreviewContextProvider>
  );
};
